import React from 'react';
import { useField } from 'formik';
import styled from 'styled-components';
import { DesignSystemBgColor, DesignSystemBorderColor } from '@minecraft.themes';
import classNames from 'classnames';
import { BodyText, FlexRow } from '@minecraft.atoms';
import {
  RadioCheckboxLabel,
  RadioCheckboxSizeValue,
  getRadioCheckOuterSize,
  getCheckHeight,
  getCheckWidth,
} from './utils';

const VARIANTS = {
  default: 'default',
  primary: 'primary',
} as const;

type Variants = keyof typeof VARIANTS;

/** Styled Components */
/* Create a custom checkbox */
type CheckmarkProps = {
  $size: RadioCheckboxSizeValue;
  $variant: Variants;
  disabled: boolean;
};

type CheckBoxColors = {
  bgColor: DesignSystemBgColor;
  bgColorChecked: DesignSystemBgColor;
  bgColorCheckedDisabled: DesignSystemBgColor;
  borderColor: DesignSystemBorderColor;
  borderColorDisabled: DesignSystemBorderColor;
  borderColorChecked: DesignSystemBorderColor;
  focusOutlineColor: DesignSystemBorderColor;
  focusOutlineColorDisabled: DesignSystemBorderColor;
};

const CHECKBOX_COLOR_MAP: Record<Variants, CheckBoxColors> = {
  default: {
    bgColor: 'card',
    bgColorChecked: 'pillSelected',
    bgColorCheckedDisabled: 'disabled',
    borderColor: 'divider',
    borderColorDisabled: 'default',
    borderColorChecked: 'divider',
    focusOutlineColor: 'divider',
    focusOutlineColorDisabled: 'default',
  },
  primary: {
    bgColor: 'card',
    bgColorChecked: 'primary',
    bgColorCheckedDisabled: 'disabled',
    borderColor: 'divider',
    borderColorDisabled: 'default',
    borderColorChecked: 'primary',
    focusOutlineColor: 'primary',
    focusOutlineColorDisabled: 'default',
  },
};

const Checkbox = styled.input<CheckmarkProps>`
  appearance: none;
  background-color: ${({ $variant, theme }): string =>
    theme.designSystem.bgColors[CHECKBOX_COLOR_MAP[$variant].bgColor]};
  margin: 0;
  font: inherit;
  border: 1px solid
    ${({ theme, disabled, $variant }): string =>
      disabled
        ? theme.designSystem.borderColors[CHECKBOX_COLOR_MAP[$variant].borderColorDisabled]
        : theme.designSystem.borderColors[CHECKBOX_COLOR_MAP[$variant].borderColor]};
  height: ${(props): string => getRadioCheckOuterSize(props.$size)};
  width: ${(props): string => getRadioCheckOuterSize(props.$size)};
  display: grid;
  place-content: center;
  cursor: ${(props): string => (props.disabled ? 'default' : 'pointer')};

  &::before {
    content: '';
    border: solid ${({ theme }): string => theme.designSystem.borderColors.inverted};
    border-width: 0 0.1875rem 0.1875rem 0;
    height: ${(props): string => getCheckHeight(props.$size)};
    width: ${(props): string => getCheckWidth(props.$size)};
    opacity: 0;
    transform: rotate(45deg);
    transition: opacity 0.3s;
  }

  &:checked {
    background-color: ${({ theme, disabled, $variant }): string =>
      disabled
        ? theme.designSystem.bgColors[CHECKBOX_COLOR_MAP[$variant].bgColorCheckedDisabled]
        : theme.designSystem.bgColors[CHECKBOX_COLOR_MAP[$variant].bgColorChecked]};
    border: 1px solid
      ${({ theme, disabled, $variant }): string =>
        disabled
          ? theme.designSystem.borderColors[CHECKBOX_COLOR_MAP[$variant].borderColorDisabled]
          : theme.designSystem.borderColors[CHECKBOX_COLOR_MAP[$variant].borderColorChecked]};
  }

  &:checked::before {
    opacity: 1;
  }

  &:focus {
    outline: max(2px, 0.15em) solid
      ${({ theme, disabled, $variant }): string =>
        disabled
          ? theme.designSystem.borderColors[CHECKBOX_COLOR_MAP[$variant].focusOutlineColorDisabled]
          : theme.designSystem.borderColors[CHECKBOX_COLOR_MAP[$variant].focusOutlineColor]};
    outline-offset: max(2px, 0.15em);
  }
`;

/** Types */
type HtmlInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'onBlur' | 'value' | 'multiple' | 'type' | 'id'
>;

export type CheckboxInputProps = Omit<HtmlInputProps, 'size'> & {
  /**
   * callback invoked with the value of the selected checkbox
   */
  onChange?: (newValue: string) => void;
  /**
   * value of this checkbox option
   */
  value: string;
  /**
   * is this option currently selected
   */
  checked: boolean;
  /**
   * label to display next to the checkbox
   */
  label?: string;
  /**
   * variant of the checkbox (default: "default")
   */
  variant?: Variants;

  /**
   * border radius of the checkbox (default: "none")
   */
  borderRadius?: 'none' | 'lg' | 'sm' | 'xs' | 'md' | 'xl' | '2xl' | '3xl' | 'full';
  /**
   * whether to render a small or large checkbox (default: "sm")
   */
  size?: RadioCheckboxSizeValue;
  id?: string;
  'data-testid'?: string;
  'data-qa-id'?: string;
  /** prevents a click event on the checkbox from propagating to parent elements
   * in most cases this is not needed, but if the checkbox is inside a clickable element
   * like a collapse panel or accordion header, you may need to set this to true
   */
  stopPropagation?: boolean;
};

/**
 * A simple, controlled checkbox input with label
 * @example
 * const [selectedValue, setSelectedValue] = useState<string>('');
 * <CheckboxInput
      value="myValue"
      onChange={() => setSelectedValue('myValue')}
      checked={selectedValue === 'myValue'}
      label="My Value"
    />
 */
export const CheckboxInput = ({
  size = 'sm' as RadioCheckboxSizeValue,
  onChange,
  'data-testid': testId,
  'data-qa-id': qaId,
  label,
  disabled,
  variant = VARIANTS.default,
  borderRadius = 'none',
  className,
  stopPropagation = false,
  ...inputProps
}: CheckboxInputProps) => {
  const validateVariant = (variantProp: string): Variants => {
    if (['default', 'primary'].includes(variantProp)) {
      return variantProp as Variants;
    }

    return VARIANTS.default;
  };
  return (
    <RadioCheckboxLabel $size={size} disabled={disabled}>
      <Checkbox
        {...inputProps}
        data-testid={testId}
        data-qa-id={qaId}
        onClick={(e) => {
          // if the user wants to stop propagation to a parent element, do so on the click event
          // because stopping onChange doesn't prevent the parent click event from firing
          if (stopPropagation) {
            e.stopPropagation();
          }
        }}
        onChange={(e) => onChange?.(e.target.value)}
        type="checkbox"
        disabled={disabled}
        $size={size}
        $variant={validateVariant(variant)}
        className={classNames(`cn_atom_rounded-${borderRadius}`, className)}
        // data-btn-variant indicates if this is the "default" or "primary" version of the button
        data-checkbox-variant={variant}
      />
      <FlexRow w="full" grow={1} className="cn_overflow-hidden">
        <BodyText truncatableNoWrap>{label}</BodyText>
      </FlexRow>
    </RadioCheckboxLabel>
  );
};

export type ConnectedCheckboxInputProps = {
  /** name of the field in the formik state */
  name: string;
} & Omit<CheckboxInputProps, 'onChange' | 'value' | 'checked'>;

/**
 * A checkbox button input with label that is connected to Formik state, this will work as a boolean toggle
 * This should only be used for a single boolean toggle checkbox field
 *
 * If you have more than one checkbox that are related, use the CheckboxGroupInput component for a field with a list of values
 *
 * @example
 * <ConnectedCheckboxInput
      name="fieldname"
      label: 'My Value'
    />
 */
export const ConnectedCheckboxInput = ({ name, label, disabled, size, ...inputProps }: ConnectedCheckboxInputProps) => {
  const [input, meta, helpers] = useField<boolean>(name);
  const { value: formikValue } = input;
  const { error } = meta;
  const hasError = !!error;
  const fieldValidation = hasError ? { invalid: true } : {};
  const isChecked = Boolean(formikValue);
  const onChange = () => {
    helpers.setValue(!isChecked);
  };

  // this prevents the console warning about switching between
  // controlled and uncontrolled input types
  let inputValue = formikValue;

  if (inputValue === undefined) {
    inputValue = null;
  }

  return (
    <CheckboxInput
      size={size}
      {...inputProps}
      {...fieldValidation}
      value=""
      name={name}
      onChange={onChange}
      checked={isChecked}
      disabled={disabled}
      label={label}
    />
  );
};
